import { Avatar, Box, IconButton, Typography } from "@mui/material";
import { FavoriteBorder, ChatBubbleOutline, IosShareOutlined } from "@mui/icons-material";
import { getMediaKind } from "../util";

interface Props {
  media?: string[];
  username?: string;
  avatarUrl?: string;
  caption?: string;
}

export default function TikTokPreview({
  media = [],
  username = "Your Account",
  avatarUrl,
  caption
}: Props) {
  const first = media[0];
  const isVideo = first ? getMediaKind(first) === "video" : false;

  return (
    <Box sx={styles.root} aria-label="TikTok preview" role="img">
      {first ? (
        <Box
          component={isVideo ? "video" : "img"}
          src={first}
          autoPlay={isVideo ? true : undefined}
          muted={isVideo ? true : undefined}
          loop={isVideo ? true : undefined}
          playsInline={isVideo ? true : undefined}
          sx={styles.media}
        />
      ) : (
        <Box sx={styles.noMedia}>
          <Typography variant="body2">No media for TikTok</Typography>
        </Box>
      )}

      <Box sx={styles.topGradient} />
      <Box sx={styles.bottomGradient} />

      <Box sx={styles.rightRail}>
        <IconButton size="small" sx={styles.actionBtn} aria-label="like">
          <FavoriteBorder fontSize="small" />
        </IconButton>
        <IconButton size="small" sx={styles.actionBtn} aria-label="comment">
          <ChatBubbleOutline fontSize="small" />
        </IconButton>
        <IconButton size="small" sx={styles.actionBtn} aria-label="share">
          <IosShareOutlined fontSize="small" />
        </IconButton>
        <Avatar src={avatarUrl} sx={styles.avatar} />
      </Box>

      <Box sx={styles.bottomBar}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography sx={styles.username}>{username}</Typography>
          <Typography
            variant="body2"
            sx={{
              opacity: 0.9,
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis"
            }}
          >
            {caption?.trim()}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

const styles = {
  root: {
    position: "relative",
    width: "100%",
    maxWidth: 360,
    aspectRatio: "9 / 16",
    bgcolor: "black",
    borderRadius: 2,
    overflow: "hidden",
    color: "common.white",
    boxShadow: 1
  },
  media: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
    backgroundColor: "black"
  },
  noMedia: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "grey.500"
  },
  topGradient: {
    pointerEvents: "none",
    position: "absolute",
    left: 0,
    right: 0,
    top: 0,
    height: 140,
    background:
      "linear-gradient(180deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.25) 50%, rgba(0,0,0,0) 100%)"
  },
  bottomGradient: {
    pointerEvents: "none",
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: 160,
    background:
      "linear-gradient(0deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.25) 50%, rgba(0,0,0,0) 100%)"
  },
  rightRail: {
    position: "absolute",
    right: 10,
    bottom: 10,
    display: "flex",
    flexDirection: "column" as const,
    gap: 2,
    alignItems: "center",
    color: "common.white",
    textShadow: "0 1px 4px rgba(0,0,0,0.9)"
  },
  bottomBar: {
    position: "absolute",
    left: 12,
    right: 12,
    bottom: 12,
    display: "flex",
    alignItems: "flex-end",
    gap: 1.5,
    color: "common.white",
    textShadow: "0 1px 4px rgba(0,0,0,0.9)"
  },
  username: {
    fontWeight: 700,
    lineHeight: 1.2
  },
  avatar: {
    width: 40,
    height: 40,
    border: "2px solid rgba(255,255,255,0.9)"
  },
  actionBtn: {
    color: "common.white",
    bgcolor: "rgba(0,0,0,0.35)",
    width: 40,
    height: 40,
    borderRadius: 12,
    "&:hover": { bgcolor: "rgba(0,0,0,0.55)" }
  }
};
